<template>
  <page-view :menu="menu">
    <template #header>
      <base-header :title="$t('message.settings')" />
    </template>

    <template #content>
      <v-row no-gutters>
        <v-col cols="12">
          <v-row align="center" no-gutters>
            <div class="icon-container mr-3">
              <v-icon color="turquisePanda" class="">mdi-lock</v-icon>
            </div>
            <span class="text-h5">{{
              $t('message.pages.qualitySettings.title')
            }}</span>
            <span class="caption ml-sm-3 ml-0 text--secondary"
              >| {{ $t('message.pages.qualitySettings.subtitle') }}</span
            >
          </v-row>
        </v-col>
        <v-col class="mt-10 pa-0" cols="12">
          <v-row align="center">
            <v-col cols="2" style="min-width: 220px">
              <span class="text-body-1 font-weight-bold">{{
                $t('message.pages.qualitySettings.secondTitle')
              }}</span>
            </v-col>
            <v-col>
              <v-divider></v-divider>
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-4">
            <v-col cols="12">
              <span class="body-2">
                {{ $t('message.pages.qualitySettings.secondSubtitle') }}
                <a target="_blank" :href="storageLink">{{
                  $t('message.pages.qualitySettings.subtitleLink1')
                }}</a>
                {{ $t('message.pages.qualitySettings.and') }}
                <a target="_blank" :href="trafficLink">{{
                  $t('message.pages.qualitySettings.subtitleLink2')
                }}</a>
                {{ $t('message.pages.qualitySettings.secondSubtitle2') }}
              </span>
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-8">
            <v-card v-if="loadings.fetchData" flat width="700" height="250">
              <loading-logo :value="200" />
            </v-card>
            <v-card
              v-else-if="errors.fetchData"
              class="d-flex flex-row justify-center align-center"
              height="250"
              width="700"
              flat
            >
              <v-row no-gutters justify="center" align="center">
                <v-col cols="8" class="d-flex justify-center align-center mb-3">
                  <span class="text-h5">{{
                    $t('message.pages.qualitySettings.errors.fetchData')
                  }}</span>
                </v-col>
                <v-col cols="12">
                  <v-row no-gutters align="center" justify="center">
                    <v-btn
                      color="primary"
                      class="ma-2 ml-3 white--text"
                      rounded
                      @click="getAccountQualities"
                    >
                      <span class="mx-2">{{ $t('message.tryAgain') }}</span>
                      <v-icon right dark> mdi-cached </v-icon>
                    </v-btn>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
            <v-card v-else flat style="width: 100%">
              <div class="container__qualities-options">
                <v-row no-gutters class="mb-4">
                  <v-col cols="4" class="d-flex align-center justify-center"
                    ><b>{{
                      $t('message.pages.qualitySettings.columnTitle1')
                    }}</b></v-col
                  >
                  <v-col
                    cols="4"
                    class="d-flex align-center justify-center text-center"
                    ><span
                      v-html="$t('message.pages.qualitySettings.columnTitle2')"
                    ></span
                  ></v-col>
                  <v-col cols="4" class="d-flex align-center justify-center">
                    <b
                      >{{ $t('message.pages.qualitySettings.columnTitle3') }}
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon x-small v-bind="attrs" v-on="on">
                            <v-icon size="18"> mdi-help-circle </v-icon>
                          </v-btn>
                        </template>
                        <v-container
                          class="text-body-2 pa-0"
                          style="max-width: 230px"
                          >{{
                            $t('message.pages.qualitySettings.storageHelp')
                          }}</v-container
                        >
                      </v-tooltip>
                    </b>
                  </v-col>
                </v-row>
                <v-row
                  v-for="(quality, i) in form.qualities"
                  no-gutters
                  :key="i"
                  class="mt-2"
                >
                  <v-col cols="4">
                    <v-card
                      flat
                      :width="quality.res === '2160p' ? '174' : '140'"
                      class="d-flex justify-end"
                    >
                      <span
                        >{{ quality.res
                        }}{{ quality.res === '2160p' ? ' (4K)' : '' }}</span
                      >
                    </v-card>
                  </v-col>
                  <v-col cols="4" class="d-flex align-center justify-center">
                    <v-checkbox
                      v-model="quality.encoding"
                      :disabled="!hasEditQualitiesPerm"
                      class="pa-0 ma-0"
                      color="primary"
                      hide-details
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="4" class="d-flex align-center justify-center">
                    <v-checkbox
                      v-model="quality.playback"
                      :disabled="!hasEditQualitiesPerm"
                      class="pa-0 ma-0"
                      color="primary"
                      hide-details
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </div>
              <v-row align="center" class="mt-12">
                <v-col cols="auto" style="min-width: 100px">
                  <span class="text-body-1 font-weight-bold">
                    {{
                      $t('message.pages.qualitySettings.originalFiles.title')
                    }}
                  </span>
                </v-col>
                <v-col>
                  <v-divider />
                </v-col>
              </v-row>
              <v-row no-gutters class="mt-4">
                <v-col cols="12">
                  <span
                    class="body-2"
                    v-html="
                      $t('message.pages.qualitySettings.originalFiles.text')
                    "
                  />
                </v-col>
                <v-col class="mt-4">
                  <v-switch
                    v-model="form.keepOriginalFiles"
                    :disabled="!hasEditQualitiesPerm"
                    class="ma-0"
                    :label="
                      $t('message.pages.qualitySettings.originalFiles.label')
                    "
                    hide-details
                  />
                </v-col>
              </v-row>
              <v-row
                no-gutters
                class="mt-12 pr-12 container__actions-btns"
                v-if="hasEditQualitiesPerm"
              >
                <v-spacer />
                <v-btn
                  class="mr-2"
                  text
                  min-width="250"
                  :disabled="loadings.saveQualities || checkIfHasChanged"
                  @click="handleUndoChanges"
                >
                  {{ $t('message.pages.video.discardChanges') }}
                </v-btn>
                <v-btn
                  :loading="loadings.saveQualities"
                  :disabled="loadings.saveQualities || verifyMinimumSelected"
                  min-width="250"
                  color="primary"
                  @click="handleSaveQualities"
                  >{{ $t('message.actions.save') }}</v-btn
                >
              </v-row>
              <v-row
                v-if="errors.updateQualities || verifyMinimumSelected"
                no-gutters
                class="mt-4 pr-2"
              >
                <v-alert
                  dense
                  outlined
                  :type="verifyMinimumSelected ? 'info' : 'error'"
                  width="100%"
                >
                  <span v-if="!verifyMinimumSelected">
                    {{
                      errMsg
                        ? errMsg
                        : $t(
                            'message.pages.qualitySettings.errors.defaultError'
                          )
                    }}
                    {{ `${$t('message.commom.code')}: ${errCode}` }}
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          class="pb-1"
                          icon
                          x-small
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon size="18" color="error">
                            mdi-help-circle
                          </v-icon>
                        </v-btn>
                      </template>
                      <v-container
                        class="text-body-2 pa-0"
                        style="max-width: 300px"
                        >{{ fullErrorMsg }}</v-container
                      >
                    </v-tooltip>
                  </span>
                  <span v-else>
                    {{
                      $t(
                        'message.pages.qualitySettings.errors.selectAtleastOne'
                      )
                    }}
                  </span>
                </v-alert>
              </v-row>
            </v-card>
          </v-row>
        </v-col>
      </v-row>
    </template>
  </page-view>
</template>

<script>
// external imports
import _ from 'lodash'

// internal imports
import linkHelper from '@/utils/linkHelper.mixin.js'

// API
import qualitiesApi from '@/services/qualitiesApi'

// Components
import LoadingLogo from '@/components/LoadingLogo.vue'
import PageView from '@/layout/PageView.vue'

export default {
  name: 'Quality',
  mixins: [linkHelper],
  components: {
    LoadingLogo,
    PageView,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
  },
  data: () => ({
    libraryId: null,
    form: {
      qualities: [
        { res: '240p', encoding: false, playback: false },
        { res: '360p', encoding: false, playback: false },
        { res: '480p', encoding: false, playback: false },
        { res: '720p', encoding: false, playback: false },
        { res: '1080p', encoding: false, playback: false },
        { res: '1440p', encoding: false, playback: false },
        { res: '2160p', encoding: false, playback: false },
      ],
      keepOriginalFiles: true,
    },
    beforeChanges: {
      qualities: [],
      keepOriginalFiles: true,
    },
    loadings: {
      fetchData: false,
      saveQualities: false,
    },
    errors: {
      fetchData: false,
      updateQualities: false,
    },
    errMsg: '',
    errCode: '502',
    fullErrorMsg: '',
    wantNavigateTo: 'Advanced',
  }),
  computed: {
    hasEditQualitiesPerm() {
      return this.$store.state.user.userPermissions.includes(
        'put-general-config'
      )
    },
    menu() {
      return this.$store.state.user.settingsNavItems
    },
    storageLink() {
      return this.getStorageLink()
    },
    trafficLink() {
      return this.getTrafficLink()
    },
    verifyMinimumSelected() {
      let anyEncoding = this.form.qualities.find((quality) => quality.encoding)
      let anyPlayback = this.form.qualities.find((quality) => quality.playback)
      return !anyEncoding || !anyPlayback
    },
    checkIfHasChanged() {
      return _.isEqual(this.beforeChanges, this.form)
    },
  },
  created() {
    this.getAccountQualities()
  },
  mounted() {
    this.$root.$on('discardChangesQualityPage', () => {
      this.confirmLeavePage()
    })
    this.$root.$on('checkIfHasUnchangedQualities', (page) => {
      this.wantNavigateTo = page
      let hasChanges = _.isEqual(this.beforeChanges, this.form)
      if (!hasChanges && !this.loadings.fetchData) {
        this.$root.$emit('unsavedChangesModal', false, true)
        this.$root.$emit('updateEmbedPreviewChange')    
      } else {
        this.confirmLeavePage()
      }
    })
  },
  beforeDestroy() {
    this.$root.$off('discardChangesQualityPage')
    this.$root.$off('checkIfHasUnchangedQualities')
  },
  methods: {
    async getAccountQualities() {
      this.clearErrors()
      this.loadings.fetchData = true
      try {
        let ret = await qualitiesApi.getAccountQualities()
        this.libraryId = ret.data.id
        let accountEncodes = ret.data.encoding
        let accountPlaybacks = ret.data.playback
        if (accountEncodes) {
          for (let encode of accountEncodes) {
            for (let quality of this.form.qualities) {
              if (encode === quality.res) {
                quality.encoding = true
              }
            }
          }
        }
        if (accountPlaybacks) {
          for (let playback of accountPlaybacks) {
            for (let quality of this.form.qualities) {
              if (playback === quality.res) {
                quality.playback = true
              }
            }
          }
        }
        this.form.keepOriginalFiles = ret.data.keep_original_files
        this.beforeChanges = JSON.parse(JSON.stringify(this.form))
        this.loadings.fetchData = false
      } catch (e) {
        this.errors.fetchData = true
        this.loadings.fetchData = false
        console.error(e)
      }
    },
    async handleSaveQualities() {
      this.clearErrors()
      this.loadings.saveQualities = true
      const keep_original_files = this.form.keepOriginalFiles
      let playback = []
      let encoding = []
      for (let quality of this.form.qualities) {
        quality.playback && playback.push(quality.res)
        quality.encoding && encoding.push(quality.res)
      }
      try {
        await qualitiesApi.updateQualities(
          { playback, encoding, keep_original_files },
          this.libraryId
        )
        this.beforeChanges = JSON.parse(JSON.stringify(this.form))
        this.loadings.saveQualities = false
      } catch (e) {
        this.loadings.saveQualities = false
        this.errors.updateQualities = true
        this.errCode = e.response.status
        this.errMsg = e.response.data.errMsg
        this.fullErrorMsg = JSON.parse(JSON.stringify(e))
        this.fullErrorMsg && delete this.fullErrorMsg.config
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    handleUndoChanges() {
      this.form = JSON.parse(JSON.stringify(this.beforeChanges))
    },
    confirmLeavePage() {
      this.$root.$emit('confirmNavigationFromQuality', this.wantNavigateTo)
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
    },
  },
}
</script>

<style lang="scss" scoped>
.container__qualities-options {
  width: 700px;
}

@media only screen and (max-width: 1217px) {
  .container__qualities-options {
    width: 100%;
    overflow-x: auto;

    > div {
      width: 100%;
      min-width: 592px;
    }
  }

  .container__actions-btns {
    flex-direction: column-reverse !important;
    padding: 0 !important;

    button:last-child {
      margin-bottom: 12px;
    }

    button {
      margin-right: 0 !important;
    }
  }
}
</style>
